/**
 * Judge.me Reviews Section
 * Custom integration with Judge.me API and Swiper.js
 */

class JudgeReviews {
  constructor(section) {
    this.section = section;

    // Get API credentials from section settings
    this.publicToken = section.dataset.apiPublicKey || '';
    this.privateToken = section.dataset.apiPrivateKey || '';
    this.shopDomain = this.extractShopDomain(section.dataset.siteUrl) || '';
    this.reviewsPerPage = parseInt(section.dataset.reviewsPerPage) || 10;

    // Get product data from section attributes
    this.productId = section.dataset.productId || null;
    this.productHandle = section.dataset.productHandle || null;
    this.productRating = section.dataset.productRating ? parseFloat(section.dataset.productRating) : null;
    this.productRatingCount = section.dataset.productRatingCount ? parseInt(section.dataset.productRatingCount) : null;

    // Get text settings from section
    // Try both camelCase and kebab-case access (for debugging)
    const avisText1Raw = section.dataset.avisText1 || section.getAttribute('data-avis-text-1');
    const avisText2Raw = section.dataset.avisText2 || section.getAttribute('data-avis-text-2');

    // Debug: Check raw dataset values
    console.log('Raw dataset values:', {
      'dataset.avisText1': section.dataset.avisText1,
      'dataset.avisText2': section.dataset.avisText2,
      'getAttribute avisText1': section.getAttribute('data-avis-text-1'),
      'getAttribute avisText2': section.getAttribute('data-avis-text-2'),
      'final avisText1Raw': avisText1Raw,
      'final avisText2Raw': avisText2Raw
    });

    this.avisText1 = avisText1Raw && avisText1Raw.trim() !== ''
      ? avisText1Raw
      : 'd\'après';
    this.avisText2 = avisText2Raw && avisText2Raw.trim() !== ''
      ? avisText2Raw
      : 'avis';

    this.carousel = section.querySelector('.judge-reviews__carousel');
    this.wrapper = section.querySelector('.swiper-wrapper');
    this.loading = section.querySelector('.judge-reviews__loading');
    this.badge = section.querySelector('.judge-reviews__badge');

    this.swiper = null;

    // Debug info
    console.log('Judge.me Reviews - Configuration:', {
      publicToken: this.publicToken.substring(0, 10) + '...',
      shopDomain: this.shopDomain,
      reviewsPerPage: this.reviewsPerPage,
      productId: this.productId,
      productHandle: this.productHandle,
      productRating: this.productRating,
      productRatingCount: this.productRatingCount,
      avisText1: this.avisText1,
      avisText2: this.avisText2
    });

    this.init();
  }

  /**
   * Extract shop domain from site URL
   * @param {string} url - Site URL (e.g., "https://sova-care.com" or "sova-care.myshopify.com")
   * @returns {string} Shop domain for Judge.me API
   */
  extractShopDomain(url) {
    if (!url) return '';

    // Remove protocol and trailing slashes
    let domain = url.replace(/^https?:\/\//, '').replace(/\/$/, '');

    // Remove path if any
    domain = domain.split('/')[0];

    // If it's already a myshopify.com domain, return as is
    if (domain.includes('.myshopify.com')) {
      return domain;
    }

    // Otherwise return the domain as is (for custom domains)
    return domain;
  }

  async init() {
    try {
      const data = await this.fetchReviews();

      if (data && data.reviews && data.reviews.length > 0) {
        this.updateBadge(data);
        this.renderReviews(data.reviews);
        this.initSwiper();
        this.hideLoading();
      } else {
        // No reviews found or error - hide entire section
        console.warn('Judge.me: No reviews found, hiding section');
        this.hideSection();
      }
    } catch (error) {
      // Error loading reviews - hide entire section
      console.error('Error loading Judge.me reviews:', error);
      this.hideSection();
    }
  }

  /**
   * Fetch reviews from Judge.me Widget API (Public Token)
   * If productId is available, fetch product-specific reviews
   * Otherwise, fetch all shop reviews
   */
  async fetchReviews() {
    let url;

    // Fetch product-specific reviews if productId is available
    if (this.productId) {
      url = `https://judge.me/api/v1/widgets/product_review?api_token=${this.publicToken}&shop_domain=${this.shopDomain}&external_id=${this.productId}&per_page=${this.reviewsPerPage}`;
      console.log('Judge.me Product Review API Request:', url);
    } else {
      // Fetch all reviews from the shop
      url = `https://judge.me/api/v1/widgets/all_reviews_page?api_token=${this.publicToken}&shop_domain=${this.shopDomain}&per_page=${this.reviewsPerPage}`;
      console.log('Judge.me All Reviews API Request:', url);
    }

    try {
      const response = await fetch(url);
      const data = await response.json();

      console.log('Judge.me Widget API Response:', data);

      // Check for different response structures
      const widgetHTML = data.widget || data.all_reviews;

      if (widgetHTML) {
        // Parse HTML widget to extract review data
        const reviews = this.parseReviewsFromHTML(widgetHTML);
        console.log(`✅ Successfully parsed ${reviews.length} reviews from widget HTML`);

        return { reviews };
      } else {
        throw new Error('No reviews data in response');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  }

  /**
   * Parse reviews from Judge.me HTML widget
   */
  parseReviewsFromHTML(html) {
    // Create a temporary DOM element to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const reviewElements = doc.querySelectorAll('.jdgm-rev');
    const reviews = [];

    reviewElements.forEach((el) => {
      try {
        // Extract rating
        const ratingEl = el.querySelector('.jdgm-rev__rating');
        const rating = ratingEl ? parseInt(ratingEl.dataset.score) : 0;

        // Extract reviewer name
        const authorEl = el.querySelector('.jdgm-rev__author');
        const name = authorEl ? authorEl.textContent.trim() : 'Anonymous';

        // Extract review title
        const titleEl = el.querySelector('.jdgm-rev__title');
        const title = titleEl ? titleEl.textContent.trim() : '';

        // Extract review body
        const bodyEl = el.querySelector('.jdgm-rev__body');
        const body = bodyEl ? bodyEl.textContent.trim() : '';

        // Extract date
        const timestampEl = el.querySelector('.jdgm-rev__timestamp');
        const created_at = timestampEl ? timestampEl.getAttribute('data-content') : new Date().toISOString();

        // Extract verified buyer status
        const verified = el.getAttribute('data-verified-buyer') === 'true' ? 'verified-purchase' : 'unconfirmed-buyer';

        // Extract pictures if any
        const pictureEls = el.querySelectorAll('.jdgm-rev-carousel__item img');
        const pictures = Array.from(pictureEls).map(img => ({
          urls: {
            small: img.src,
            original: img.src
          }
        }));

        reviews.push({
          rating,
          title,
          body,
          verified,
          created_at,
          pictures: pictures.length > 0 ? pictures : [],
          reviewer: {
            name
          }
        });
      } catch (err) {
        console.warn('Error parsing review element:', err);
      }
    });

    return reviews;
  }

  /**
   * Update rating badge with average rating and review count
   * Uses product metafields if available, otherwise calculates from reviews
   */
  updateBadge(data) {
    const ratingValue = this.section.querySelector('.judge-reviews__rating-value');
    const ratingLink = this.section.querySelector('.judge-reviews__rating-link');
    const starsContainer = this.section.querySelector('.judge-reviews__stars');

    // Prioritize product metafields, fallback to calculated values
    let averageRating = this.productRating || 0;
    let reviewCount = this.productRatingCount || 0;

    // If metafields not available, calculate from reviews
    if (!averageRating && data.reviews && data.reviews.length > 0) {
      const sum = data.reviews.reduce((acc, review) => acc + review.rating, 0);
      averageRating = sum / data.reviews.length;
    }

    if (!reviewCount && data.reviews) {
      reviewCount = data.reviews.length;
    }

    // Update rating value
    if (ratingValue && averageRating > 0) {
      ratingValue.textContent = averageRating.toFixed(1);
    }

    // Update review count link
    if (ratingLink) {
      const linkText = reviewCount > 0
        ? `${reviewCount} ${this.avisText2}`
        : `-- ${this.avisText2}`;

      console.log('Updating rating link:', {
        reviewCount,
        avisText2: this.avisText2,
        finalText: linkText
      });

      ratingLink.textContent = linkText;
    }

    // Update stars
    if (starsContainer && averageRating > 0) {
      starsContainer.innerHTML = this.generateStars(averageRating, 'large');
      starsContainer.dataset.rating = averageRating;
    }
  }

  /**
   * Render all review cards
   */
  renderReviews(reviews) {
    if (!this.wrapper) return;

    this.wrapper.innerHTML = '';

    reviews.forEach(review => {
      const card = this.createReviewCard(review);
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(card);
      this.wrapper.appendChild(slide);
    });
  }

  /**
   * Create individual review card
   */
  createReviewCard(review) {
    const card = document.createElement('div');
    card.className = 'judge-reviews__card';

    // Card Header
    const header = document.createElement('div');
    header.className = 'judge-reviews__card-header';

    const user = document.createElement('div');
    user.className = 'judge-reviews__card-user';

    // Avatar
    const avatar = document.createElement('div');
    avatar.className = 'judge-reviews__avatar';
    const avatarText = document.createElement('p');
    avatarText.className = 'judge-reviews__avatar-text';
    avatarText.textContent = this.getInitials(review.reviewer.name || 'Anonymous');
    avatar.appendChild(avatarText);

    // User Info
    const userInfo = document.createElement('div');
    userInfo.className = 'judge-reviews__user-info';

    const userHeader = document.createElement('div');
    userHeader.className = 'judge-reviews__user-header';

    const userName = document.createElement('p');
    userName.className = 'judge-reviews__user-name';
    userName.textContent = review.reviewer.name || 'Anonymous';

    const rating = document.createElement('div');
    rating.className = 'judge-reviews__card-rating';
    rating.innerHTML = this.generateStars(review.rating, 'small');

    userHeader.appendChild(userName);
    userHeader.appendChild(rating);

    const date = document.createElement('p');
    date.className = 'judge-reviews__date';
    date.textContent = this.formatDate(review.created_at);

    userInfo.appendChild(userHeader);
    userInfo.appendChild(date);

    user.appendChild(avatar);
    user.appendChild(userInfo);
    header.appendChild(user);

    // Card Body
    const body = document.createElement('div');
    body.className = 'judge-reviews__card-body';

    const content = document.createElement('div');
    content.className = 'judge-reviews__card-content';

    // Review Title
    if (review.title) {
      const title = document.createElement('h3');
      title.className = 'judge-reviews__review-title';
      title.textContent = review.title;
      content.appendChild(title);
    }

    // Review Content
    if (review.body) {
      const text = document.createElement('p');
      text.className = 'judge-reviews__review-content';
      text.textContent = review.body;
      content.appendChild(text);
    }

    body.appendChild(content);

    // Review Images
    if (review.pictures && review.pictures.length > 0) {
      const images = document.createElement('div');
      images.className = 'judge-reviews__images';

      review.pictures.slice(0, 2).forEach(picture => {
        const img = document.createElement('img');
        img.className = 'judge-reviews__image';
        img.src = picture.urls.small || picture.urls.original;
        img.alt = 'Photo du client';
        img.loading = 'lazy';
        images.appendChild(img);
      });

      body.appendChild(images);
    }

    card.appendChild(header);
    card.appendChild(body);

    return card;
  }

  /**
   * Generate star rating HTML
   * @param {number} rating - Rating value (0-5)
   * @param {string} size - 'large' or 'small'
   */
  generateStars(rating, size = 'large') {
    const starSize = size === 'large' ? 22 : 14;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHTML = '';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      starsHTML += this.createStarSVG(starSize, 'full');
    }

    // Half star
    if (hasHalfStar) {
      starsHTML += this.createStarSVG(starSize, 'half');
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      starsHTML += this.createStarSVG(starSize, 'empty');
    }

    return starsHTML;
  }

  /**
   * Create star SVG with stroke (bordure) as per Figma design
   */
  createStarSVG(size, type) {
    const className = size === 22 ? 'judge-reviews__star' : 'judge-reviews__card-star';
    const strokeWidth = size === 22 ? '1.2' : '1';

    if (type === 'full') {
      return `
        <div class="${className}">
          <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 0.375L10.1564 5.68237L15.875 6.13656L11.6094 9.87513L12.8844 15.4891L8 12.625L3.11562 15.4891L4.39062 9.87513L0.125 6.13656L5.84362 5.68237L8 0.375Z" fill="#352045" stroke="#352045" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>
          </svg>
        </div>
      `;
    } else if (type === 'half') {
      return `
        <div class="${className}">
          <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="half-${size}">
                <stop offset="50%" stop-color="#352045"/>
                <stop offset="50%" stop-color="transparent"/>
              </linearGradient>
            </defs>
            <path d="M8 0.375L10.1564 5.68237L15.875 6.13656L11.6094 9.87513L12.8844 15.4891L8 12.625L3.11562 15.4891L4.39062 9.87513L0.125 6.13656L5.84362 5.68237L8 0.375Z" fill="url(#half-${size})" stroke="#352045" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>
          </svg>
        </div>
      `;
    } else {
      return `
        <div class="${className}">
          <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 0.375L10.1564 5.68237L15.875 6.13656L11.6094 9.87513L12.8844 15.4891L8 12.625L3.11562 15.4891L4.39062 9.87513L0.125 6.13656L5.84362 5.68237L8 0.375Z" fill="transparent" stroke="#352045" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>
          </svg>
        </div>
      `;
    }
  }

  /**
   * Get initials from name
   */
  getInitials(name) {
    if (!name) return 'AN';

    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /**
   * Parse date string safely for all browsers including Safari/iOS
   * Safari doesn't support "YYYY-MM-DD HH:MM:SS UTC" format
   * @param {string} dateString - Date string in various formats
   * @returns {Date|null} - Parsed Date object or null if invalid
   */
  parseDateSafari(dateString) {
    if (!dateString) return null;

    // If it's already a Date object
    if (dateString instanceof Date) {
      return isNaN(dateString.getTime()) ? null : dateString;
    }

    // If it's a number (timestamp)
    if (typeof dateString === 'number') {
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? null : d;
    }

    // Convert to string if needed
    const str = String(dateString).trim();

    // 1. Judge.me format: "2025-12-09 17:19:05 UTC" (Safari doesn't support this)
    const judgeMeMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})\s*(?:UTC)?$/);
    if (judgeMeMatch) {
      const [, year, month, day, hour, minute, second] = judgeMeMatch;
      const d = new Date(Date.UTC(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hour, 10),
        parseInt(minute, 10),
        parseInt(second, 10)
      ));
      return isNaN(d.getTime()) ? null : d;
    }

    // 2. ISO 8601 format: "2024-01-15T10:30:00Z" or "2024-01-15T10:30:00.000Z"
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?(?:Z|([+-]\d{2}:\d{2}))?)?$/);
    if (isoMatch) {
      const [, year, month, day, hour = 0, minute = 0, second = 0] = isoMatch;
      const d = new Date(Date.UTC(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hour, 10),
        parseInt(minute, 10),
        parseInt(second, 10)
      ));
      return isNaN(d.getTime()) ? null : d;
    }

    // 3. Date only: "2024-01-15"
    const dateOnlyMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      return isNaN(d.getTime()) ? null : d;
    }

    // 4. European format: "15/01/2024" or "15-01-2024"
    const euMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (euMatch) {
      const [, day, month, year] = euMatch;
      const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      return isNaN(d.getTime()) ? null : d;
    }

    // 5. Timestamp string: "1705312200000"
    if (/^\d{10,13}$/.test(str)) {
      const timestamp = parseInt(str, 10);
      const ms = str.length === 10 ? timestamp * 1000 : timestamp;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }

    // 6. Fallback: try native Date parsing
    const fallback = new Date(str);
    if (!isNaN(fallback.getTime())) {
      return fallback;
    }

    // 7. Last resort: try replacing dashes with slashes
    const slashVersion = str.replace(/-/g, '/');
    const slashDate = new Date(slashVersion);
    if (!isNaN(slashDate.getTime())) {
      return slashDate;
    }

    return null;
  }

  /**
   * Format date according to current locale (FR, IT, UK)
   * Compatible Safari/iOS - fixes NaN bug with Judge.me date format
   */
  formatDate(dateString) {
    const date = this.parseDateSafari(dateString);

    if (!date) {
      console.warn('Invalid date string:', dateString);
      return '--/--/----';
    }

    // Detect current locale from Shopify or browser
    let locale = 'fr-FR'; // Default to French

    if (typeof Shopify !== 'undefined' && Shopify.locale) {
      // Map Shopify locales to browser locales
      const localeMap = {
        'fr': 'fr-FR',
        'it': 'it-IT',
        'en': 'en-GB'
      };
      locale = localeMap[Shopify.locale] || 'fr-FR';
    } else {
      // Fallback: detect from URL or navigator
      const urlLocale = window.location.hostname;
      if (urlLocale.includes('.it')) {
        locale = 'it-IT';
      } else if (urlLocale.includes('.co.uk')) {
        locale = 'en-GB';
      }
    }

    // Format date according to locale
    try {
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      // Ultimate fallback: manual formatting
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }

  /**
   * Initialize Swiper carousel
   */
  initSwiper() {
    if (!this.carousel || typeof Swiper === 'undefined') {
      console.error('Swiper is not loaded or carousel element not found');
      return;
    }

    const isMobile = window.innerWidth < 989;

    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 'auto',
      spaceBetween: isMobile ? 12 : 18,
      loop: false,
      grabCursor: true,
      breakpoints: {
        640: {
          slidesPerView: 'auto',
          spaceBetween: 12,
        },
        989: {
          slidesPerView: 'auto',
          spaceBetween: 18,
        },
        1200: {
          slidesPerView: 'auto',
          spaceBetween: 18,
        }
      },
      navigation: {
        nextEl: this.section.querySelector('.judge-reviews__nav-button--next'),
        prevEl: this.section.querySelector('.judge-reviews__nav-button--prev'),
      },
      pagination: {
        el: this.section.querySelector('.judge-reviews__pagination'),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      },
    });
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    if (this.loading) {
      this.loading.style.display = 'none';
    }
  }

  /**
   * Hide entire section (when no reviews found)
   */
  hideSection() {
    // Find the Shopify section wrapper (parent of this.section)
    const shopifySection = this.section.closest('.shopify-section');
    if (shopifySection) {
      shopifySection.style.display = 'none';
      console.log('✅ Judge.me section hidden (no reviews found)');
    } else {
      // Fallback: hide the section element itself
      this.section.style.display = 'none';
    }
  }
}

// Initialize all Judge Reviews sections on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initJudgeReviews);
} else {
  initJudgeReviews();
}

function initJudgeReviews() {
  const sections = document.querySelectorAll('[data-section-type="w-judge-reviews"]');
  sections.forEach(section => {
    new JudgeReviews(section);
  });
}

// Re-initialize on Shopify section events (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="w-judge-reviews"]');
    if (section) {
      new JudgeReviews(section);
    }
  });
}